import { Navigate } from "react-router-dom";
import { Card, LoadingSpinner } from "@vms/ui-web";
import { useAuth } from "../auth/AuthProvider";
import { useOrgContext } from "../context/OrgContext";
import { OperationsOverview } from "./admin/OperationsOverview";

/**
 * `/` resolves differently per persona:
 *   • FPZERO admin → render Operations Overview directly (this is their home).
 *   • Single-org user → redirect to `/org/:slug`.
 *   • Multi-org user → render an org chooser (placeholder; richer UX later).
 *   • No-org user → render an "awaiting access" notice.
 */
export function HomeRedirect() {
  const { user } = useAuth();
  const { orgs, orgsLoading } = useOrgContext();

  if (!user) return null;
  if (user.isFpzeroAdmin) return <OperationsOverview />;
  if (orgsLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    );
  }
  if (orgs.length === 1) return <Navigate to={`/org/${orgs[0].slug}`} replace />;
  if (orgs.length === 0) {
    return (
      <Card className="p-6 max-w-lg mx-auto mt-12">
        <div className="font-semibold text-gray-900 mb-2">Awaiting access</div>
        <div className="text-sm text-gray-600">
          Your account isn't a member of any organisation yet. Ask your org
          admin to add you, or contact support@fpzero.co.uk.
        </div>
      </Card>
    );
  }
  return (
    <div className="space-y-4 max-w-2xl mx-auto mt-8">
      <div className="text-lg font-semibold text-gray-900">Choose an organisation</div>
      <div className="grid gap-3">
        {orgs.map((org) => (
          <a
            key={org.id}
            href={`/org/${org.slug}`}
            className="block rounded-xl border border-gray-200 bg-white px-4 py-3 hover:border-primary hover:shadow-sm transition"
          >
            <div className="font-medium text-gray-900">{org.name}</div>
            <div className="text-xs text-gray-500">{org.slug}</div>
          </a>
        ))}
      </div>
    </div>
  );
}
